import { Checkbox, Table } from 'antd';
import { LoadingSpinnerComponent } from 'components';
import { DEFAULT_PAGE_SIZE_OPTIONS, EXPAND_ICON } from 'constants/constant';
import React, { useEffect, useState } from 'react';
import { TableProps } from 'type/tableType';
import './Table.css';
import TableHeader from './TableHeader';
import { useTable } from './tableHandler';

type TableComponentType = <T extends object>(props: TableProps<T>) => JSX.Element;

const CustomTable: TableComponentType = <T extends object>({
  name,
  columns,
  dataFetch,
  searchParams,
  setSearchParams,
  options,
  actions,
  additionalButton,
}: TableProps<T>): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<T[]>([]);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedList, setSelectedList] = useState<number[]>([]);

  useEffect(() => {
    actions?.onSelectionChange?.(selectedList);
  }, [selectedList, actions]);

  const { onPageChange, handleSelectAll, handleSelect } = useTable<T>({
    dataSource,
    setSearchParams,
    setSelectAll,
    setSelectedList,
  });

  // 체크박스 클릭 시 이벤트 전파 막기
  const handleSelectAllClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    handleSelectAll(e as any);
  };

  const idColumn = {
    title: (
      <div onClick={handleSelectAllClick} style={{ cursor: 'pointer' }}>
        <Checkbox checked={selectAll} onChange={handleSelectAll}>
          ID
        </Checkbox>
      </div>
    ),
    dataIndex: 'id',
    key: 'id',
    width: '1%',
    render: (_text: string, record: T): React.ReactNode => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          type="checkbox"
          value={(record as { id: number }).id}
          checked={selectedList.includes((record as { id: number }).id)}
          onChange={e => {
            e.stopPropagation(); // 이벤트 버블링 방지
            handleSelect((record as { id: number }).id);
          }}
        />
        <div style={{ paddingLeft: '10px', alignItems: 'center' }}>
          {(record as { id: number }).id}
        </div>
      </div>
    ),
    sorter: true,
  };

  const columnsWithId = options?.isDisableIdColumn ? columns : [idColumn, ...columns];

  // Get sort order from URL params
  const orderBy = searchParams.get('order_by');
  const orderDirection = searchParams.get('order_direction');

  // Apply sort order to columns
  const columnsWithSortOrder = columnsWithId.map(column => {
    if (column.sorter && orderBy && column.dataIndex === orderBy) {
      const sortOrder =
        orderDirection === 'asc' ? 'ascend' : orderDirection === 'desc' ? 'descend' : undefined;
      return {
        ...column,
        sortOrder: sortOrder as 'ascend' | 'descend' | undefined,
        sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[], // Only cycle between ascend and descend
      };
    }
    return {
      ...column,
      sortOrder: undefined,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[], // Only cycle between ascend and descend
    };
  });

  useEffect(() => {
    setIsLoading(true);
    dataFetch(searchParams)
      .then(({ data, count }: { data: T[]; count: number }) => {
        setDataSource(data);
        setTotalSize(count);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching data:', error);
        setDataSource([]);
        setTotalSize(0);
        setIsLoading(false);
      });
  }, [searchParams, dataFetch]);

  return (
    <>
      {isLoading ? (
        <LoadingSpinnerComponent />
      ) : (
        <>
          <TableHeader
            name={name}
            additionalButton={additionalButton}
            searchParams={searchParams}
            options={options}
            actions={actions}
            setSearchParams={setSearchParams}
          />
          <Table
            dataSource={dataSource}
            columns={columnsWithSortOrder}
            pagination={{
              current: Number(searchParams.get('page')) || 1,
              pageSize: Number(searchParams.get('page_size')) || 100,
              total: totalSize,
              showSizeChanger: true,
              pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
            }}
            rowKey="id"
            expandable={
              actions?.onRowExpand
                ? {
                    expandedRowRender: actions?.onRowExpand,
                    expandIcon: ({ expanded, onExpand, record }) => (
                      <span
                        onClick={e => onExpand(record, e)}
                        style={{ cursor: 'pointer', paddingRight: '8px' }}
                      >
                        {expanded ? EXPAND_ICON.EXPANDED : EXPAND_ICON.COLLAPSED}
                      </span>
                    ),
                  }
                : undefined
            }
            onRow={
              actions?.onRowClick
                ? record => ({
                    onClick: () => actions.onRowClick?.(record),
                    style: { cursor: 'pointer' },
                  })
                : undefined
            }
            onChange={onPageChange}
          />
        </>
      )}
    </>
  );
};

export default CustomTable;
